import type { ReactNode } from "react";

// A deliberately small markdown renderer. The guides use exactly three
// constructs — ## headings, ### headings and paragraphs — so pulling in a full
// markdown library (and a sanitiser to go with it) would be more risk and
// weight than the job needs. Anything unrecognised renders as a paragraph,
// and nothing is ever injected as raw HTML.
export function Prose({ markdown }: { markdown: string }) {
  const blocks = markdown.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);

  return (
    <div className="space-y-5">
      {blocks.map((block, i) => {
        if (block.startsWith("### ")) {
          return (
            <h3
              key={i}
              className="li-serif pt-3 text-xl text-[#1C2430] md:text-2xl"
            >
              {block.slice(4)}
            </h3>
          );
        }
        if (block.startsWith("## ")) {
          return (
            <h2
              key={i}
              className="li-serif pt-5 text-2xl text-[#1C2430] md:text-3xl"
            >
              {block.slice(3)}
            </h2>
          );
        }
        if (block.startsWith("# ")) {
          return (
            <h2 key={i} className="li-serif pt-5 text-2xl text-[#1C2430] md:text-3xl">
              {block.slice(2)}
            </h2>
          );
        }
        // Lists show up occasionally in the closing advice section.
        if (/^[-*]\s/.test(block)) {
          const items = block.split("\n").map((l) => l.replace(/^[-*]\s*/, ""));
          return (
            <ul key={i} className="ml-5 list-disc space-y-2">
              {items.map((it, j) => (
                <li key={j} className="text-[17px] leading-relaxed text-[#1C2430]/90">
                  {inline(it)}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="max-w-[68ch] text-[17px] leading-relaxed text-[#1C2430]/90">
            {inline(block)}
          </p>
        );
      })}
    </div>
  );
}

// **bold** only. Rendered as React nodes rather than HTML, so a stray
// asterisk in club copy can never become markup.
function inline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") && p.length > 4 ? (
      <strong key={i} className="font-semibold text-[#1C2430]">
        {p.slice(2, -2)}
      </strong>
    ) : (
      p
    ),
  );
}
